import { useEffect, useState } from 'react';
import { geocodeAddress, getWeatherForecast } from '../services/weatherService';
import { utcToUkTime } from '../utils/dateUtils';

/**
 * One-line forecast for the game hero ("11°C · Light rain · 9 mph wind"),
 * from the same weather service the classic WeatherCard uses. Null while
 * loading, when there's no address, or when the forecast isn't available.
 */
export function useGameWeather(venueAddress: string | null | undefined, gameDate: string | null | undefined): string | null {
  const [line, setLine] = useState<string | null>(null);

  useEffect(() => {
    if (!venueAddress || !gameDate) {
      setLine(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const geo = await geocodeAddress(venueAddress);
        if (!geo) return;
        const f = await getWeatherForecast(geo.lat, geo.lon, utcToUkTime(new Date(gameDate)));
        if (!f || cancelled) return;
        // weatherService returns placeholder objects when the API fails: one with
        // this description, and a seasonal mock with a random (non-integer)
        // wind direction. Real API directions are whole degrees. Show nothing
        // rather than invented weather.
        if (f.description === 'Weather data unavailable' || !Number.isInteger(f.windDirection)) {
          setLine(null);
          return;
        }
        const desc = f.description ? f.description.charAt(0).toUpperCase() + f.description.slice(1) : f.condition;
        // The service returns wind in m/s; show mph.
        const mph = Math.round((f.windSpeed ?? 0) * 2.237);
        setLine(`${Math.round(f.temperature)}°C · ${desc} · ${mph} mph wind`);
      } catch {
        if (!cancelled) setLine(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [venueAddress, gameDate]);

  return line;
}
